import { faKey } from "@fortawesome/free-solid-svg-icons"
import { useMemo } from "react"
import { AdminPermissions } from "../../../Enum/AdminPermissionsType"
import { AdminEmployeeTypePresets } from "../../../Objects/AdminEmployee/AdminEmployeeTypePresets"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { TitleChildrenPath } from "../../../components/Titles/Titles"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"

type Props = {
  value: string[]
  onChange: (permissions: string[]) => void
  disabled?: boolean
}

const areSamePermissions = (first: string[], second: string[]) => {
  if (first.length !== second.length) return false

  const secondSet = new Set(second)

  return first.every((item) => secondSet.has(item))
}

const allAdminPermissions = Object.values(AdminPermissions)

export function AdminEmployeePermissions({ value, onChange, disabled }: Props) {
  const { t } = useTranslation("adminEmployee")

  const presetOptions = useMemo(
    () => [
      { label: t("rolePreset.custom"), value: "custom" },
      { label: t("rolePreset.analytics"), value: "analytics" },
      { label: t("rolePreset.marketing"), value: "marketing" },
      { label: t("rolePreset.support"), value: "support" },
      { label: t("rolePreset.commercial"), value: "commercial" },
      { label: t("rolePreset.infrastructure"), value: "infrastructure" },
    ],
    [t],
  )

  const selectedPreset = useMemo(() => {
    const matched = Object.entries(AdminEmployeeTypePresets).find(([, permissions]) =>
      areSamePermissions(value, permissions),
    )

    return matched?.[0] ?? "custom"
  }, [value])

  const togglePermission = (permission: string) => {
    if (disabled) return

    onChange(value.includes(permission) ? value.filter((p) => p !== permission) : [...value, permission])
  }

  const handlePresetChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const presetValue = event.target.value

    if (presetValue === "custom") return

    onChange(AdminEmployeeTypePresets[presetValue])
  }

  return (
    <TitleChildrenPath
      icon={faKey}
      iconColor="#000000"
      boxShadow="none"
      title={t("label_title")}
      children={
        <div className="mt-1 p-2 flex flex-col gap-4">
          <GenericDropDown
            data={presetOptions}
            initialState={selectedPreset}
            handlerFuncion={handlePresetChange}
            disable={disabled}

            descriptionKey="interactionDescriptions:pages.adminDashboard.adminStaff.adminEmployeePermissions.select.legend"
          />

          <Animate type={AnimationTypes.fadeIn} duration="0.6s">
            <div className="rounded-lg border border-orange-100 bg-orange-50/40 p-3 shadow-sm">
              <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(260px,1fr))]">
                {allAdminPermissions.map((permission) => {
                  const enabled = value.includes(permission)

                  return (
                    <div
                      key={permission}
                      className={`flex items-center justify-between px-2 py-3 rounded-xl ring-2 transition-all duration-300 ${
                        enabled ? "ring-orange-500 bg-orange-50 shadow-sm" : "ring-slate-200 bg-white"
                      }`}
                    >
                      <span className="text-sm font-bold text-slate-800">{t(permission)}</span>

                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => togglePermission(permission)}
                        className={`relative w-[42px] h-[22px] rounded-full transition-all duration-300 ${
                          enabled ? "bg-linear-to-r from-red-600 to-orange-500" : "bg-slate-400"
                        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                        data-interaction-description-key="interactionDescriptions:pages.adminDashboard.adminStaff.adminEmployeePermissions.button.togglePermission"
                      >
                        <span
                          className={`absolute top-[2px] left-[2px] w-[18px] h-[18px] bg-white rounded-full shadow-sm transition-all duration-300 ${
                            enabled ? "translate-x-[20px]" : "translate-x-0"
                          }`}
                        />
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          </Animate>
        </div>
      }
    />
  )
}
